import { Trash2 } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { CategoryChip } from "@/components/add/CategoryChip";
import { AccountPicker } from "@/components/add/AccountPicker";
import { filterCategoriesByType } from "@/lib/categoryFilter";

const TYPE_META = {
  income: { color: "#16a34a", en: "Income", ar: "دخل" },
  revenue: { color: "#0ea5e9", en: "Revenue", ar: "إيراد" },
  expense: { color: "#dc2626", en: "Expense", ar: "مصروف" },
  borrow: { color: "#7c3aed", en: "Borrowed", ar: "اقتراض" },
  lend: { color: "#0891b2", en: "Lent", ar: "إقراض" },
  recurring: { color: "#4f46e5", en: "Monthly", ar: "التزام شهري" },
  debt: { color: "#7c3aed", en: "Borrowed", ar: "اقتراض" },
};
const TYPES = ["expense", "income", "revenue", "borrow", "lend", "recurring"];

export function VoiceConfirmRow({ row, cats, ccys, onChange, onRemove, index, userCcy, rates }) {
  const { lang } = useI18n();
  const meta = TYPE_META[row.type] || TYPE_META.expense;
  const set = (k, v) => onChange({ ...row, [k]: v });
  const inputCls = "w-full px-3 py-2 rounded-xl neu-inset text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a8a55]";

  const rowCcy = (row.currency || "AED").toUpperCase();
  const baseCcy = (userCcy || "AED").toUpperCase();
  const rate = rates?.[rowCcy];
  const converted = rowCcy !== baseCcy && rate ? row.amount / rate : null;
  const convStr = converted != null
    ? `≈ ${new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(converted)} ${baseCcy}`
    : null;

  return (
    <div className="neu-sm rounded-2xl p-4 flex gap-3">
      <div className="w-1.5 rounded-full shrink-0" style={{ background: meta.color }} />
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex items-center gap-2">
          <select data-testid={`row-${index}-type`} value={row.type} onChange={(e) => set("type", e.target.value)}
            className={inputCls + " w-28 font-semibold"} style={{ color: meta.color }}>
            {TYPES.map((t) => <option key={t} value={t}>{TYPE_META[t][lang] || TYPE_META[t].en}</option>)}
          </select>
          <button data-testid={`voice-drop-${index}`} onClick={onRemove}
            className="ms-auto p-2 rounded-xl hover:bg-red-50 text-red-500">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
        <input data-testid={`row-${index}-desc`} value={row.description || ""} onChange={(e) => set("description", e.target.value)}
          placeholder={lang === "ar" ? "الوصف" : "Description"} className={inputCls + " font-medium"} />
        <div className="grid grid-cols-[1fr_auto] gap-2">
          <input data-testid={`row-${index}-amount`} type="number" step="0.01" min="0"
            value={row.amount} onChange={(e) => set("amount", parseFloat(e.target.value) || 0)}
            className={inputCls + " font-display font-bold text-lg tabular-nums text-end"} style={{ color: meta.color }} />
          <select data-testid={`row-${index}-currency`} value={row.currency} onChange={(e) => set("currency", e.target.value)}
            className={inputCls + " w-24 font-semibold text-center"}>
            {ccys.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
          </select>
        </div>
        {convStr && (
          <p data-testid={`row-${index}-fx`} className="text-xs font-semibold text-slate-500 tabular-nums text-end -mt-1">
            {convStr}
          </p>
        )}
        <div>
          <p className="text-xs font-semibold text-slate-500 mb-1">{lang === "ar" ? "التاريخ" : "Date"}</p>
          <input data-testid={`row-${index}-date`} type="date" value={row.date || ""}
            onChange={(e) => set("date", e.target.value)} className={inputCls + " tabular-nums"} />
        </div>
        <CategoryChip value={row.category} onChange={(v) => set("category", v)} cats={filterCategoriesByType(cats, row.type)} tid={`row-${index}-category`} />
        <div>
          <p className="text-xs font-semibold text-slate-500 mb-1">{lang === "ar" ? "الحساب / المحفظة" : "Account / Wallet"}</p>
          <AccountPicker value={row.account_id || ""} onChange={(v) => set("account_id", v || "")} testId={`row-${index}-account`} />
          {!row.account_id && (
            <p data-testid={`row-${index}-account-error`} className="text-[11px] font-semibold text-red-600 mt-1">
              {lang === "ar" ? "اختر الحساب لتتمكن من الحفظ" : "Select an account to save"}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
